// UI-side formatting helpers — port of v1 inline Vue methods.
import {
  formatEventDateDisplayCompact,
  formatDateDDMMYYYY,
  formatJobNotifyLabel,
  getJobDisplayName,
  isJobPaymentOverdue,
  isPersonalJob,
  jobMatchesQuery,
  normalizeJobStatus,
  parseEventDateStart,
  parseNumber,
  JOB_STATUS_AWAITING,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_CANCELLED,
} from '@qjob/shared';
import type { Job } from '@qjob/shared';

export {
  formatEventDateDisplayCompact,
  formatDateDDMMYYYY,
  formatJobNotifyLabel,
  getJobDisplayName,
  isJobPaymentOverdue,
  isPersonalJob,
  jobMatchesQuery,
  normalizeJobStatus,
  parseEventDateStart,
  parseNumber,
  JOB_STATUS_AWAITING,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_CANCELLED,
};

export function getJobClientName(job: Partial<Job> | null | undefined): string {
  if (!job) return '';
  return String(job['Contact Name'] || '').trim() || String(job.Client || '').trim();
}

export function getJobTypeLabel(job: Partial<Job> | null | undefined): string {
  if (!job) return '';
  return String(job.JobType || '').trim();
}

export function formatEventDate(dateStr: unknown): string {
  if (!dateStr) return '-';
  return formatEventDateDisplayCompact(dateStr);
}

export function formatPaymentDate(dateStr: unknown): string {
  return formatDateDDMMYYYY(dateStr);
}

export function formatPrice(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'number' ? value : parseNumber(value);
  if (!num) return String(value);
  return num.toLocaleString('en-US');
}

export function formatPriceOrFree(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'ฟรี';
  return formatPrice(value);
}

export function formatCurrency(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  return formatPrice(value);
}

export function getEffectivePaymentDueDate(job: Partial<Job>): string {
  return String(job['Payment Due Date'] || '');
}

export function getJobRowKey(job: Partial<Job>): string {
  const sheetName = String(job._sheetName || '').trim();
  const rowIndex = Number(job._rowIndex || 0);
  if (sheetName && Number.isInteger(rowIndex) && rowIndex > 1) {
    return `row:${sheetName}:${rowIndex}`;
  }
  const jobId = String(job.JobID || '').trim();
  return jobId ? `job:${jobId}` : `row:${sheetName}:${rowIndex || ''}`;
}

// Ports v1's getJobFolderUrl — the Drive folder holding this job's generated
// documents. "Folder URL" is the primary field; older/alternate field names
// are checked as a fallback for jobs saved before that field existed.
export function getJobFolderUrl(job: Partial<Job> | null | undefined): string {
  if (!job) return '';
  const candidates = [job['Folder URL'], job['Quotation URL']];
  for (const c of candidates) {
    const s = String(c || '').trim();
    if (s && s.includes('drive.google.com') && s.includes('folders')) return s;
  }
  return '';
}

export function hasAnyJobDocument(job: Partial<Job> | null | undefined): boolean {
  if (!job) return false;
  return !!(
    job['Quotation PDF URL'] ||
    job['Invoice PDF URL'] ||
    job['Receipt PDF URL'] ||
    getJobFolderUrl(job)
  );
}

export function statusBadgeClass(status: unknown): string {
  const s = normalizeJobStatus(status);
  if (s === JOB_STATUS_COMPLETED) return 'bg-success/10 text-success border-success/30';
  if (s === JOB_STATUS_CANCELLED) return 'bg-danger/10 text-danger border-danger/30';
  return 'bg-warning/10 text-warning border-warning/30';
}

// localStorage-backed job drafts (replaces v1 jobDrafts).
const DRAFTS_KEY = 'qjob.v2.jobDrafts';

export interface JobDraft {
  id: string;
  savedAt: number;
  job: Record<string, any>;
}

export function loadJobDrafts(): JobDraft[] {
  try {
    const raw = localStorage.getItem(DRAFTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveJobDrafts(drafts: JobDraft[]): void {
  try {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  } catch {
    // Quota exceeded or storage disabled — silently ignore.
  }
}

export function addJobDraft(job: Record<string, any>): JobDraft {
  const drafts = loadJobDrafts();
  const draft: JobDraft = {
    id: `draft-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    savedAt: Date.now(),
    job,
  };
  drafts.unshift(draft);
  saveJobDrafts(drafts);
  return draft;
}

export function removeJobDraft(id: string): void {
  const drafts = loadJobDrafts().filter((d) => d.id !== id);
  saveJobDrafts(drafts);
}

/** Update an existing draft in place (keeps its id/position). Returns null if not found. */
export function updateJobDraft(id: string, job: Record<string, any>): JobDraft | null {
  const drafts = loadJobDrafts();
  const idx = drafts.findIndex((d) => d.id === id);
  if (idx === -1) return null;
  drafts[idx] = { ...drafts[idx], savedAt: Date.now(), job };
  saveJobDrafts(drafts);
  return drafts[idx];
}

export function getDraftTitle(draft: JobDraft): string {
  return (
    String(draft.job.Client || '').trim() ||
    String(draft.job['Item / Job Title'] || '').trim() ||
    'ร่างไม่มีชื่อ'
  );
}

export function getDraftSubtitle(draft: JobDraft): string {
  const parts: string[] = [];
  if (draft.job.JobType) parts.push(String(draft.job.JobType).trim());
  if (draft.job['Event Date']) parts.push(formatEventDate(draft.job['Event Date']));
  if (draft.job['Price per Unit']) parts.push(formatPrice(draft.job['Price per Unit']) + ' บาท');
  return parts.length > 0 ? parts.join(' · ') : 'ยังไม่ระบุรายละเอียด';
}

export function formatDraftSavedAt(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'เมื่อสักครู่';
  if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} ชม.ที่แล้ว`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay} วันที่แล้ว`;
  return formatDateDDMMYYYY(d);
}
